import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useHistory } from 'react-router-dom';
import Button from 'components/Button';
import {
  FocusableActionsWrapper,
  StyledButtonWrapper,
  StyledButtonText,
  StyledInputWrapper,
  StyledInput,
  StyledInputCursor,
  StyledInputText,
  StyledInputInvalidMessage,
} from './StyledComponents';
import innerAnalyticsVolia from 'helpers/innerAnalyticsVolia';

const ActionsSection = (props) => <FocusableActionsWrapper {...props} />;

ActionsSection.Button = ({ onEnter, onFocus, label, focusOnMouseEnter, disabled, isActive, className }) => (
  <StyledButtonWrapper>
    <Button
      onEnter={onEnter}
      onFocus={onFocus}
      focusOnMouseEnter={focusOnMouseEnter}
      disabled={disabled}
      isActive={isActive}
      className={className}
    >
      <StyledButtonText>{label}</StyledButtonText>
    </Button>
  </StyledButtonWrapper>
);

ActionsSection.Button.propTypes = {
  onEnter: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  focusOnMouseEnter: PropTypes.bool,
  disabled: PropTypes.bool,
};

ActionsSection.Link = ({
  onFocus,
  label,
  to,
  analyticsEventName,
  analyticsEventGroup,
  analyticsEventValue,
  focusOnMouseEnter,
  disabled,
}) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const history = useHistory();
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const onEnter = useCallback(() => {
    innerAnalyticsVolia.postEvent({
      eventGroup: analyticsEventGroup,
      eventName: analyticsEventName,
      eventValue: analyticsEventValue,
    });

    history.push(to);
  }, [analyticsEventGroup, analyticsEventName, analyticsEventValue, history, to]);

  return (
    <StyledButtonWrapper>
      <Button onEnter={onEnter} onFocus={onFocus} focusOnMouseEnter={focusOnMouseEnter} disabled={disabled}>
        <StyledButtonText>{label}</StyledButtonText>
      </Button>
    </StyledButtonWrapper>
  );
};

ActionsSection.Link.defaultProps = {
  focusOnMouseEnter: true,
};

ActionsSection.Link.propTypes = {
  to: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
  onFocus: PropTypes.func,
  focusOnMouseEnter: PropTypes.bool,
  disabled: PropTypes.bool,
};

ActionsSection.Input = ({
  onEnter,
  onFocus,
  value,
  placeholder,
  isValid,
  invalidMessage,
  cursor,
  cursorPosition,
  align = 'left',
  type = 'dark',
}) => {
  let valueComponent;
  const cursorComponent = <StyledInputCursor key="cursor" />;

  if (cursor && value) {
    const valueWithCursorArray = [...value.slice(0, cursorPosition), cursorComponent, ...value.slice(cursorPosition)];
    valueComponent = (
      <StyledInputText type={type} align={align} isActive>
        {valueWithCursorArray}
      </StyledInputText>
    );
  } else {
    valueComponent = (
      <StyledInputText type={type} align={align}>
        {cursor && cursorComponent}
        {value || placeholder}
      </StyledInputText>
    );
  }
  return (
    <StyledInputWrapper>
      <StyledInput type={type} onEnter={onEnter} onFocus={onFocus} isValid={isValid}>
        {valueComponent}
      </StyledInput>
      {!isValid && <StyledInputInvalidMessage>{invalidMessage}</StyledInputInvalidMessage>}
    </StyledInputWrapper>
  );
};

ActionsSection.Input.propTypes = {
  onEnter: PropTypes.func,
  onFocus: PropTypes.func,
  isValid: PropTypes.bool,
  invalidMessage: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  placeholder: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
  cursor: PropTypes.bool,
  cursorPosition: PropTypes.number,
  align: PropTypes.string,
  type: PropTypes.oneOf(['light', 'dark']),
};

ActionsSection.Input.defaultProps = {
  isValid: true,
  cursor: false,
  cursorPosition: 0,
};

export default ActionsSection;
